import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '@/api/client';
import { useUpdateAdminLocation } from '@/api/opengater/useLocations';
import { Input } from '@/components/primitives/Input';
import { Select } from '@/components/primitives/Select';
import { Toggle } from '@/components/primitives/Toggle';
import { ListRow, PanelForm, SaveBar, SettingsGroup, SettingsNote, SettingsRow, } from '@/components/settings/Settings';
import { buildChangedPatch, locationAdminError, requiredTextError } from './locationRules';
function draftOf(card) {
    return {
        flag: card.flag ?? '',
        name: card.name,
        description: card.description ?? '',
        speed: card.speed == null ? '' : String(card.speed),
        is_premium: card.is_premium,
        is_geo_routed: card.is_geo_routed,
        group_id: card.group_id == null ? '' : String(card.group_id),
        group_order: String(card.group_order),
    };
}
function normalized(draft) {
    return {
        flag: draft.flag.trim() || null,
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        speed: draft.speed.trim() === '' ? null : Number(draft.speed),
        is_premium: draft.is_premium,
        is_geo_routed: draft.is_geo_routed,
        group_id: draft.group_id === '' ? null : Number(draft.group_id),
        group_order: Number(draft.group_order),
    };
}
function validationFields(error) {
    if (!(error instanceof ApiError) || error.status !== 422)
        return {};
    const detail = error.body?.detail;
    if (!Array.isArray(detail))
        return {};
    const fields = {};
    for (const issue of detail) {
        if (!issue || typeof issue !== 'object')
            continue;
        const loc = issue.loc;
        const msg = issue.msg;
        const field = Array.isArray(loc) ? loc.at(-1) : null;
        if (typeof field !== 'string' || typeof msg !== 'string')
            continue;
        if (field in draftOfDummy) {
            fields[field] = msg.replace(/^Value error,\s*/u, '');
        }
    }
    return fields;
}
const draftOfDummy = {
    flag: '',
    name: '',
    description: '',
    speed: '',
    is_premium: false,
    is_geo_routed: false,
    group_id: '',
    group_order: '',
};
export function LocationOverview({ card, groups, onDirtyChange, onOpenGroup, }) {
    const incoming = useMemo(() => draftOf(card), [card]);
    const [base, setBase] = useState(incoming);
    const [draft, setDraft] = useState(incoming);
    const [touched, setTouched] = useState(() => new Set());
    const [fieldErrors, setFieldErrors] = useState({});
    const update = useUpdateAdminLocation();
    useEffect(() => {
        setBase((current) => {
            const next = { ...current };
            for (const key of Object.keys(incoming)) {
                if (!touched.has(key))
                    next[key] = incoming[key];
            }
            return next;
        });
        setDraft((current) => {
            const next = { ...current };
            for (const key of Object.keys(incoming)) {
                if (!touched.has(key))
                    next[key] = incoming[key];
            }
            return next;
        });
    }, [incoming, touched]);
    const patch = useMemo(() => buildChangedPatch(normalized(base), normalized(draft), ['flag', 'description', 'speed', 'group_id']), [base, draft]);
    const nameError = requiredTextError(draft.name) ?? fieldErrors.name;
    const orderError = !draft.group_order.trim() || !Number.isInteger(Number(draft.group_order))
        ? 'Укажите целый порядок'
        : fieldErrors.group_order;
    const speedError = draft.speed.trim() !== '' && !Number.isFinite(Number(draft.speed))
        ? 'Укажите число'
        : fieldErrors.speed;
    const dirty = Object.keys(patch).length > 0;
    useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
    const change = (key, value) => {
        setDraft((current) => ({ ...current, [key]: value }));
        setTouched((current) => new Set(current).add(key));
        setFieldErrors((current) => {
            if (!current[key])
                return current;
            const next = { ...current };
            delete next[key];
            return next;
        });
        update.reset();
    };
    const reset = () => {
        setBase(incoming);
        setDraft(incoming);
        setTouched(new Set());
        setFieldErrors({});
        update.reset();
    };
    const submit = async (event) => {
        event.preventDefault();
        if (!dirty || nameError || orderError || speedError || update.isPending)
            return;
        try {
            await update.mutateAsync({ id: card.id, patch });
            setTouched(new Set());
            setFieldErrors({});
        }
        catch (error) {
            setFieldErrors(validationFields(error));
        }
    };
    const generalError = update.error && Object.keys(fieldErrors).length === 0
        ? locationAdminError(update.error, 'Не удалось сохранить локацию.')
        : undefined;
    return (_jsxs(PanelForm, { meta: `Локация #${card.id}`, onSubmit: submit, children: [_jsxs(SettingsGroup, { title: "\u041E\u0441\u043D\u043E\u0432\u043D\u044B\u0435 \u0434\u0430\u043D\u043D\u044B\u0435", children: [_jsx(SettingsRow, { label: "\u0424\u043B\u0430\u0433", hint: "\u0414\u0432\u0443\u0445\u0431\u0443\u043A\u0432\u0435\u043D\u043D\u044B\u0439 \u043A\u043E\u0434", wide: true, error: fieldErrors.flag, children: _jsx(Input, { "aria-label": "\u0424\u043B\u0430\u0433 \u043B\u043E\u043A\u0430\u0446\u0438\u0438", value: draft.flag, disabled: update.isPending, onChange: (event) => change('flag', event.target.value) }) }), _jsx(SettingsRow, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", wide: true, error: nameError, errorId: "location-name-error", children: _jsx(Input, { "aria-label": "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u043B\u043E\u043A\u0430\u0446\u0438\u0438", "aria-invalid": Boolean(nameError) || undefined, "aria-errormessage": nameError ? 'location-name-error' : undefined, value: draft.name, disabled: update.isPending, onChange: (event) => change('name', event.target.value) }) }), _jsx(SettingsRow, { label: "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435", wide: true, error: fieldErrors.description, children: _jsx(Input, { "aria-label": "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435 \u043B\u043E\u043A\u0430\u0446\u0438\u0438", value: draft.description, disabled: update.isPending, onChange: (event) => change('description', event.target.value) }) }), _jsx(SettingsRow, { label: "\u0421\u043A\u043E\u0440\u043E\u0441\u0442\u044C", suffix: "\u041C\u0431\u0438\u0442/\u0441", error: speedError, children: _jsx(Input, { "aria-label": "\u0421\u043A\u043E\u0440\u043E\u0441\u0442\u044C", type: "number", value: draft.speed, disabled: update.isPending, onChange: (event) => change('speed', event.target.value) }) }), _jsx(SettingsRow, { label: "\u0413\u0440\u0443\u043F\u043F\u0430", error: fieldErrors.group_id, children: _jsxs(Select, { "aria-label": "\u0413\u0440\u0443\u043F\u043F\u0430", value: draft.group_id, disabled: update.isPending, onChange: (event) => change('group_id', event.target.value), children: [_jsx("option", { value: "", children: "\u0411\u0435\u0437 \u0433\u0440\u0443\u043F\u043F\u044B" }), groups.map((group) => (_jsx("option", { value: String(group.id), children: group.name }, group.id)))] }) }), _jsx(SettingsRow, { label: "\u041F\u043E\u0440\u044F\u0434\u043E\u043A \u0432 \u0433\u0440\u0443\u043F\u043F\u0435", error: orderError, children: _jsx(Input, { "aria-label": "\u041F\u043E\u0440\u044F\u0434\u043E\u043A \u0432 \u0433\u0440\u0443\u043F\u043F\u0435", type: "number", value: draft.group_order, disabled: update.isPending, onChange: (event) => change('group_order', event.target.value) }) }), _jsx(SettingsRow, { label: "\u041F\u0440\u0435\u043C\u0438\u0430\u043B\u044C\u043D\u0430\u044F", error: fieldErrors.is_premium, children: _jsx(Toggle, { "aria-label": "\u041F\u0440\u0435\u043C\u0438\u0430\u043B\u044C\u043D\u0430\u044F \u043B\u043E\u043A\u0430\u0446\u0438\u044F", on: draft.is_premium, disabled: update.isPending, onChange: (value) => change('is_premium', value) }) }), _jsx(SettingsRow, { label: "\u0413\u0435\u043E\u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0438\u0437\u0430\u0446\u0438\u044F", error: fieldErrors.is_geo_routed, children: _jsx(Toggle, { "aria-label": "\u0413\u0435\u043E\u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0438\u0437\u0430\u0446\u0438\u044F", on: draft.is_geo_routed, disabled: update.isPending, onChange: (value) => change('is_geo_routed', value) }) })] }), card.group && (_jsx(SettingsGroup, { title: "\u0422\u0435\u043A\u0443\u0449\u0430\u044F \u0433\u0440\u0443\u043F\u043F\u0430", children: _jsx(ListRow, { title: card.group.name, sub: `${card.group.translations.length} переводов`, onOpen: () => onOpenGroup(card.group.id), ariaLabel: `Изменить группу ${card.group.name}` }) })), generalError && _jsx(SettingsNote, { tone: "error", children: generalError }), _jsx(SaveBar, { dirty: dirty, saving: update.isPending, saved: update.isSuccess, canSave: dirty && !nameError && !orderError && !speedError && !update.isPending, error: generalError, onReset: reset })] }));
}
